import React, { useState, useEffect } from 'react';
import { Spinner, useToast } from '@chakra-ui/react';
import { MdLeaderboard, MdLanguage, MdSettings } from 'react-icons/md';
import { FiUser, FiX, FiSave } from 'react-icons/fi';
import axios from 'axios';

const Leaderboard = () => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('male'); // 'male' or 'female'
  const [language, setLanguage] = useState('all');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [rankNames, setRankNames] = useState({
    rank1: '1',
    rank2: '2',
    rank3: '3'
  });
  const [savingSettings, setSavingSettings] = useState(false);


const languages = [
  { value: 'all', label: 'All Languages' },
  { value: 'English', label: 'English' },
  { value: 'Hindi', label: 'Hindi' },
  { value: 'Telugu', label: 'Telugu' },
  { value: 'Tamil', label: 'Tamil' },
  { value: 'Kannada', label: 'Kannada' },
  { value: 'Malayalam', label: 'Malayalam' },
  { value: 'Marathi', label: 'Marathi' },
  { value: 'Bengali', label: 'Bengali' },
  { value: 'Gujarati', label: 'Gujarati' },
  { value: 'Punjabi', label: 'Punjabi' },
  { value: 'Oriya', label: 'Oriya' },
  { value: 'Bhojpuri', label: 'Bhojpuri' },
];



  const fetchRankNames = async () => {
  try {
    const res = await axios.get('/api/v2/rank/leaderboard-settings');
    if (res.data.success && res.data.data) {
      setRankNames({
        rank1: res.data.data.rank1Name || '1',
        rank2: res.data.data.rank2Name || '2',
        rank3: res.data.data.rank3Name || '3'
      });
    }
  } catch (error) {
    console.error('Error fetching rank names:', error);
  }
};




const saveRankNames = async () => {
  try {
    setSavingSettings(true);

    const res = await axios.post('/api/v2/rank/leaderboard-settings', {
      rank1Name: rankNames.rank1,
      rank2Name: rankNames.rank2,
      rank3Name: rankNames.rank3
    });

    if (res.data.success) {
      toast({
        title: 'Success',
        description: 'Rank names updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setShowSettings(false);
    }
  } catch (error) {
    toast({
      title: 'Error',
      description: 'Failed to update rank names',
      status: 'error',
      duration: 3000,
      isClosable: true,
    });
  } finally {
    setSavingSettings(false);
  }
};


  const fetchLeaderboard = async () => {
  try {
    setLoading(true);

    const endpoint =
      activeTab === "male"
        ? "api/v1/app/coin-earnings/leaderboard/male"
        : "api/v1/app/coin-earnings/leaderboard/female";

    const res = await axios.get(endpoint, {
      params: {
        language: language, // English, Hindi, Marathi etc.
      },
    });

    if (res.data.success) {
      setUsers(res.data.data || []);
    }
  } catch (error) {
    console.error("Error fetching leaderboard:", error);
  } finally {
    setLoading(false);
  }
};



  useEffect(() => {
    fetchLeaderboard();
  }, [activeTab, language]);

  useEffect(() => {
    fetchRankNames();
  }, []);

  return (
    <div className="min-h-screen p-6 bg-white text-[var(--black-text)] relative">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-extrabold text-[var(--primary-solid)] flex items-center gap-3 mb-2">
              <MdLeaderboard />
              Leader Board
            </h1>
            <p className="text-[var(--black-text)]/70">
              View the top performers ranked by {activeTab === 'male' ? 'spent' : 'earned'} coins
            </p>
          </div>
          
          <button
            onClick={() => setShowSettings(true)}
            className="flex items-center gap-2 px-6 py-3 bg-[var(--primary-solid)] text-white rounded-2xl font-black text-sm hover:opacity-90 transition shadow-lg shadow-[var(--primary-solid)]/20"
          >
            <MdSettings className="text-xl" />
            Edit Rank Names
          </button>
        </div>

        {/* Settings Modal */}
        {showSettings && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white w-full max-w-md rounded-[32px] shadow-2xl p-8 border-2 border-gray-100">
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-2xl font-black text-gray-800 flex items-center gap-3">
                  <MdSettings className="text-[var(--primary-solid)]" />
                  Custom Rank Names
                </h2>
                <button 
                  onClick={() => setShowSettings(false)}
                  className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 hover:text-gray-600 transition"
                >
                  <FiX className="text-xl" />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-3 px-1">Rank 1 Name (Gold)</label>
                  <input
                    type="text"
                    value={rankNames.rank1}
                    onChange={(e) => setRankNames({...rankNames, rank1: e.target.value})}
                    placeholder="e.g. King, Champion"
                    className="w-full px-5 py-4 rounded-2xl bg-gray-50 border-2 border-gray-100 focus:border-[var(--primary-solid)] focus:outline-none font-bold text-gray-700 transition"
                  />
                </div>
                <div>
                  <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-3 px-1">Rank 2 Name (Silver)</label>
                  <input
                    type="text"
                    value={rankNames.rank2}
                    onChange={(e) => setRankNames({...rankNames, rank2: e.target.value})}
                    placeholder="e.g. Queen, Pro"
                    className="w-full px-5 py-4 rounded-2xl bg-gray-50 border-2 border-gray-100 focus:border-[var(--primary-solid)] focus:outline-none font-bold text-gray-700 transition"
                  />
                </div>
                <div>
                  <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-3 px-1">Rank 3 Name (Bronze)</label>
                  <input
                    type="text"
                    value={rankNames.rank3}
                    onChange={(e) => setRankNames({...rankNames, rank3: e.target.value})}
                    placeholder="e.g. Prince, Star"
                    className="w-full px-5 py-4 rounded-2xl bg-gray-50 border-2 border-gray-100 focus:border-[var(--primary-solid)] focus:outline-none font-bold text-gray-700 transition"
                  />
                </div>
              </div>

              <button
                onClick={saveRankNames}
                disabled={savingSettings}
                className="w-full mt-10 py-5 bg-gradient-to-r from-[var(--primary-solid)] to-[var(--primary-dark)] text-white rounded-2xl font-black shadow-xl shadow-[var(--primary-solid)]/30 hover:shadow-2xl hover:shadow-[var(--primary-solid)]/40 transition-all active:scale-95 flex items-center justify-center gap-3 disabled:opacity-50"
              >
                <FiSave className="text-xl" />
                {savingSettings ? 'SAVING...' : 'SAVE CHANGES'}
              </button>
            </div>
          </div>
        )}

        {/* Filters and Tabs */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-8">
          <div className="flex bg-gray-100 p-1.5 rounded-2xl w-full md:w-auto shadow-inner">
            <button
              onClick={() => setActiveTab('male')}
              className={`flex-1 md:flex-none px-10 py-3 rounded-xl text-sm font-black transition-all duration-300 ${
                activeTab === 'male'
                  ? 'bg-white text-[var(--primary-solid)] shadow-md transform scale-105'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Male (Spent)
            </button>
            <button
              onClick={() => setActiveTab('female')}
              className={`flex-1 md:flex-none px-10 py-3 rounded-xl text-sm font-black transition-all duration-300 ${
                activeTab === 'female'
                  ? 'bg-white text-[var(--primary-solid)] shadow-md transform scale-105'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Female (Earned)
            </button>
          </div>

          <div className="flex items-center gap-4 w-full md:w-auto">
            <div className="bg-gray-50 flex items-center gap-3 px-4 py-3 rounded-2xl border-2 border-gray-100 w-full shadow-sm">
              <MdLanguage className="text-2xl text-[var(--primary-solid)]" />
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="bg-transparent text-sm font-bold text-gray-700 focus:outline-none w-full cursor-pointer"
              >
                {languages.map((lang) => (
                  <option key={lang.value} value={lang.value}>
                    {lang.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Leaderboard Table Container */}
        <div className="border-2 rounded-3xl overflow-hidden shadow-xl">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 bg-gray-50/50">
              <Spinner size="xl" color="var(--primary-solid)" thickness="6px" />
              <p className="mt-6 text-gray-500 font-bold tracking-wide">CALCULATING RANKINGS...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-24 bg-gray-50/50">
              <div className="bg-white w-24 h-24 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg rotate-12">
                <FiUser className="text-4xl text-gray-200" />
              </div>
              <h3 className="text-xl font-black text-gray-800 mb-2">NO DATA FOUND</h3>
              <p className="text-gray-500 font-medium max-w-xs mx-auto">No users match the selected gender and language filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="py-4 border-b-2 border-gray-100">
                    <th className="px-8 py-5 text-xs font-black text-gray-400 uppercase tracking-widest">Rank</th>
                    <th className="px-8 py-5 text-xs font-black text-gray-400 uppercase tracking-widest">User Details</th>
                    <th className="px-8 py-5 text-xs font-black text-gray-400 uppercase tracking-widest text-center">Language</th>
                    <th className="px-8 py-5 text-xs font-black text-gray-400 uppercase tracking-widest text-right">
                      {activeTab === 'male' ? 'Total Spent' : 'Total Earned'}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {users.map((user, index) => (
                    <tr key={user._id} className="group hover:bg-cyan-900-500 transition-all duration-300">
                      <td className="px-8 py-6">
                        <div className={`px-4 min-w-[3rem] h-12 rounded-2xl flex items-center justify-center font-black text-sm uppercase transition-transform group-hover:scale-110 shadow-sm ${
                          index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-white' :
                          index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-white' :
                          index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white' :
                          'bg-gray-50 text-gray-400 border border-gray-100'
                        }`}>
                          {index === 0 ? rankNames.rank1 : 
                           index === 1 ? rankNames.rank2 : 
                           index === 2 ? rankNames.rank3 : 
                           index + 1}
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <img
                              src={user.profileImage && user.profileImage !== 'default-profile.jpg' ? user.profileImage : 'https://via.placeholder.com/48'}
                              alt=""
                              className="w-14 h-14 rounded-2xl object-cover border-4 border-white shadow-md"
                            />
                            {index < 3 && (
                                <div className="absolute -top-2 -right-2 text-2xl">👑</div>
                            )}
                          </div>
                          <div>
                            <p className="font-black text-gray-800 text-lg leading-tight group-hover:text-[var(--primary-solid)] transition-colors">
                              {user.fullName || 'Anonymous User'}
                            </p>
                            <p className="text-sm text-gray-500 font-bold">{user.phoneNumber || 'No phone number'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6 text-center">
                        <span className="inline-block px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider bg-blue-50 text-blue-600 border border-blue-100">
                          {user.language || 'N/A'}
                        </span>
                      </td>
                      <td className="px-8 py-6 text-right">
                        <div className="flex flex-col items-end">
                          <div className="flex items-center gap-2">
                            <span className={`text-2xl font-black ${activeTab === 'male' ? 'text-rose-600' : 'text-emerald-600'}`}>
                              {(activeTab === 'male'
  ? user.totalSpentCoins
  : user.totalEarnedCoins
)?.toLocaleString() || 0}
                            </span>
                            <div className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center text-[10px] text-white font-bold shadow-sm">C</div>
                          </div>
                          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1">Total Coins</p>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
